import { DatePicker } from '@/components/ui/date-picker';
import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatCurrency, formatMonth, getPaymentTypeLabel, getMonthOptions } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  BarChart3, 
  Download,
  TrendingUp,
  TrendingDown,
  Calendar,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  FileText,
  Loader2
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  ComposedChart,
  Area
} from 'recharts';
import { financialRange, financialPeriod, validateFinancialReport, saveFinancialCsv, saveFinancialPdf } from '../lib/financialExport';
import { MobileCardContainer, MobileCard, MobileCardHeader, MobileCardTitle, MobileCardContent, MobileCardRow } from '../components/MobileCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#1A4D2E', '#E07A5F', '#2A9D8F', '#E9C46A'];

const periodOptions = [
  { value: 'all', label: 'Semua Periode' },
  { value: 'this_month', label: 'Bulan Ini' },
  { value: 'last_month', label: 'Bulan Kemarin' },
  { value: 'last_3_months', label: '3 Bulan Terakhir' },
  { value: 'last_6_months', label: '6 Bulan Terakhir' },
  { value: 'this_year', label: 'Tahun Ini' },
  { value: 'custom', label: 'Custom' },
];

const Laporan = () => {
  const [data, setData] = useState(null);
  const [profitData, setProfitData] = useState(null);
  const [expenseData, setExpenseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const requestRef = useRef(0);
  const [report, setReport] = useState(null);
  const [reportRange, setReportRange] = useState({ start: null, end: null });
  const [loadError, setLoadError] = useState('');
  const [loadedKey, setLoadedKey] = useState('');
  const filterKey = `${periodFilter}|${customRange.start}|${customRange.end}`;
  const exportDisabled = loading || exportingPdf || !!loadError || !report || loadedKey !== filterKey;

  const fetchData = async () => {
    const request = ++requestRef.current;
    setLoading(true); setLoadError(''); setReport(null); setProfitData(null); setData(null); setExpenseData(null);
    try {
      const range = financialRange(periodFilter, customRange);
      const params = {};
      if (range.start) params.start_date = range.start;
      if (range.end) params.end_date = range.end;
      const response = await axios.get(`${API_URL}/api/reports/summary`, { params });
      const snapshot = validateFinancialReport(response.data);
      if (request !== requestRef.current) return;
      setReport(snapshot); setReportRange(range); setLoadedKey(filterKey);
      setData(snapshot.income); setProfitData(snapshot.profit); setExpenseData(snapshot.expense);
    } catch (error) {
      if (request !== requestRef.current) return;
      setLoadError(error.response ? 'Laporan belum berhasil dimuat. Silakan coba lagi.' : error.message || 'Laporan belum berhasil dimuat.');
    } finally { if (request === requestRef.current) setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    return () => { requestRef.current += 1; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodFilter, customRange]);

  const handlePeriodChange = (value) => {
    setPeriodFilter(value);
    if (value === 'custom') {
      setShowCustomPicker(false);
    } else {
      setShowCustomPicker(false);
    }
  };

  const pieData = data?.by_type ? Object.entries(data.by_type).map(([key, value]) => ({
    name: getPaymentTypeLabel(key),
    value
  })) : [];

  const expensePieData = expenseData?.by_category ? Object.entries(expenseData.by_category)
    .filter(([_, value]) => value > 0)
    .map(([key, value]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value
    })) : [];

  const chartData = data?.monthly_data?.map(item => ({
    month: formatMonth(item.month),
    Sewa: item.sewa,
    Listrik: item.listrik,
    Air: item.air,
    Lainnya: item.lainnya
  })) || [];

  const handleExport = () => {
    if (exportDisabled) return;
    try { saveFinancialCsv(report, reportRange); toast.success('CSV lengkap berhasil diunduh'); }
    catch (error) { toast.error(error.message || 'Gagal membuat CSV'); }
  };
  const handleExportPdf = async () => {
    if (exportDisabled) return;
    setExportingPdf(true);
    try { await saveFinancialPdf(report, reportRange); toast.success('Laporan PDF berhasil diunduh'); }
    catch (error) { toast.error(error.message || 'Gagal membuat PDF'); }
    finally { setExportingPdf(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="laporan-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Laporan Keuangan</h1>
          <p className="text-muted-foreground text-sm mt-1">Analisis pendapatan dan pengeluaran kost Anda</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} disabled={exportDisabled} variant="outline" size="sm" className="rounded-xl" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button 
            onClick={handleExportPdf} 
            size="sm"
            className="rounded-xl" 
            disabled={exportDisabled}
            data-testid="export-pdf-btn"
          >
            {exportingPdf ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-1" />
            )}
            PDF
          </Button>
        </div>
      </div>

      {/* Period Filter */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={periodFilter} onValueChange={handlePeriodChange}>
          <SelectTrigger className="w-[160px] sm:w-[180px]" data-testid="period-filter">
            <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Pilih Periode" />
          </SelectTrigger>
          <SelectContent>
            {periodOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {periodFilter === 'custom' && (
          <Popover open={showCustomPicker} onOpenChange={setShowCustomPicker}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                {customRange.start && customRange.end 
                  ? `${customRange.start} - ${customRange.end}`
                  : 'Pilih Tanggal'
                }
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 max-w-[calc(100vw-24px)] rounded-2xl" align="start">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="report-start">Tanggal Mulai</Label>
                  <DatePicker
                     id="report-start"
                    value={customRange.start}
                    onChange={(e) => setCustomRange(prev => ({ ...prev, start: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="report-end">Tanggal Akhir</Label>
                  <DatePicker
                     id="report-end"
                    value={customRange.end}
                    onChange={(e) => setCustomRange(prev => ({ ...prev, end: e.target.value }))}
                  />
                </div>
                <Button onClick={() => { setShowCustomPicker(false); fetchData(); }} className="w-full">
                  Terapkan
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
        
        {periodFilter !== 'all' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPeriodFilter('all')}
          >
            Reset
          </Button>
        )}
      </div>

      <p className="text-xs leading-relaxed text-muted-foreground">Pendapatan berdasarkan bulan tagihan yang sudah lunas; pengeluaran berdasarkan tanggal biaya. Bukan laporan arus kas. {report && !loading && `Periode: ${financialPeriod(reportRange)}.`}</p>
      {loadError ? <Card className="p-6 space-y-3" role="alert"><p>{loadError}</p><Button variant="outline" onClick={fetchData}>Coba lagi</Button></Card> : <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex flex-col">
            {loading ? (
              <>
                <Skeleton className="h-8 w-24 mb-1" />
                <Skeleton className="h-4 w-20" />
              </>
            ) : (
              <>
                <p className="text-xl md:text-2xl font-bold text-green-600">
                  {formatCurrency(data?.total_income || 0)}
                </p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  Total Pendapatan
                </p>
              </>
            )}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex flex-col">
            {loading ? (
              <>
                <Skeleton className="h-8 w-24 mb-1" />
                <Skeleton className="h-4 w-20" />
              </>
            ) : (
              <>
                <p className="text-xl md:text-2xl font-bold text-red-600">
                  {formatCurrency(expenseData?.total_expense || 0)}
                </p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  Total Pengeluaran
                </p>
              </>
            )}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex flex-col">
            {loading ? (
              <>
                <Skeleton className="h-8 w-24 mb-1" />
                <Skeleton className="h-4 w-20" />
              </>
            ) : (
              <>
                <p className={`text-xl md:text-2xl font-bold ${((data?.total_income || 0) - (expenseData?.total_expense || 0)) >= 0 ? 'text-primary' : 'text-red-600'}`}>
                  {formatCurrency((data?.total_income || 0) - (expenseData?.total_expense || 0))}
                </p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  Profit Bersih
                </p>
              </>
            )}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex flex-col">
            {loading ? (
              <>
                <Skeleton className="h-8 w-16 mb-1" />
                <Skeleton className="h-4 w-20" />
              </>
            ) : (
              <>
                <p className="text-xl md:text-2xl font-bold">
                  {data?.payment_count || 0} / {expenseData?.expense_count || 0}
                </p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  Transaksi (In/Out)
                </p>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Profit Margin Section */}
      {profitData && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <Percent className="w-5 h-5" />
            Analisis Profit Margin
          </h2>
          
          {/* Profit Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Card className="p-3 md:p-4">
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs md:text-sm text-muted-foreground">Total Pendapatan</p>
                  <ArrowUpRight className="w-4 h-4 text-green-600" />
                </div>
                <p className="text-base md:text-xl font-bold text-green-600">
                  {formatCurrency(profitData.summary?.total_income || 0)}
                </p>
              </div>
            </Card>
            <Card className="p-3 md:p-4">
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs md:text-sm text-muted-foreground">Total Pengeluaran</p>
                  <ArrowDownRight className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-base md:text-xl font-bold text-red-600">
                  {formatCurrency(profitData.summary?.total_expense || 0)}
                </p>
              </div>
            </Card>
            <Card className="p-3 md:p-4">
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs md:text-sm text-muted-foreground">Total Profit</p>
                  <Wallet className="w-4 h-4 text-primary" />
                </div>
                <p className={`text-base md:text-xl font-bold ${(profitData.summary?.total_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(profitData.summary?.total_profit || 0)}
                </p>
              </div>
            </Card>
            <Card className="p-3 md:p-4">
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs md:text-sm text-muted-foreground">Margin Keseluruhan</p>
                  <Percent className="w-4 h-4 text-primary" />
                </div>
                <p className={`text-base md:text-xl font-bold ${(profitData.summary?.avg_margin_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {profitData.summary?.total_income ? `${profitData.summary.avg_margin_percent}%` : '—'}
                </p>
              </div>
            </Card>
          </div>

          {/* Profit Margin Chart */}
          <Card className="p-6">
            <CardHeader className="p-0 pb-6">
              <CardTitle className="font-heading text-lg">Pendapatan vs Pengeluaran (Periode Terpilih)</CardTitle>
              <CardDescription>Perbandingan pendapatan dan biaya operasional bulanan</CardDescription>
            </CardHeader>
            <CardContent className="p-0 h-[350px]">
              {profitData.monthly_data?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={profitData.monthly_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month_name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))" 
                      fontSize={12}
                      tickFormatter={(value) => `${(value / 1000000).toFixed(0)}jt`}
                    />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value), name === 'income' ? 'Pendapatan' : name === 'expense' ? 'Pengeluaran' : 'Profit']}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '0.75rem'
                      }}
                    />
                    <Legend formatter={(value) => value === 'income' ? 'Pendapatan' : value === 'expense' ? 'Pengeluaran' : 'Profit'} />
                    <Bar dataKey="income" fill="#22c55e" name="income" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expense" fill="#ef4444" name="expense" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="profit" stroke="#3b82f6" strokeWidth={3} name="profit" dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Belum ada data profit margin
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly Profit Table */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Detail Profit Bulanan</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Mobile Cards View */}
              <MobileCardContainer className="mb-0">
                {profitData.monthly_data?.map((item) => (
                  <MobileCard key={item.month} className="bg-muted/30">
                    <MobileCardHeader>
                      <MobileCardTitle className="font-semibold">{item.month_name}</MobileCardTitle>
                      <Badge variant={item.margin_percent >= 50 ? 'default' : item.margin_percent >= 0 ? 'secondary' : 'destructive'}>
                        {item.income ? `${item.margin_percent}%` : '—'}
                      </Badge>
                    </MobileCardHeader>
                    <MobileCardContent>
                      <MobileCardRow label="Pendapatan" value={formatCurrency(item.income)} valueClassName="text-green-600" />
                      <MobileCardRow label="Pengeluaran" value={formatCurrency(item.expense)} valueClassName="text-red-600" />
                      <MobileCardRow label="Profit" value={formatCurrency(item.profit)} valueClassName={item.profit >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'} />
                    </MobileCardContent>
                  </MobileCard>
                ))}
              </MobileCardContainer>

              {/* Desktop Table View */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bulan</TableHead>
                      <TableHead className="text-right">Pendapatan</TableHead>
                      <TableHead className="text-right">Pengeluaran</TableHead>
                      <TableHead className="text-right">Profit</TableHead>
                      <TableHead className="text-right">Margin</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profitData.monthly_data?.map((item) => (
                      <TableRow key={item.month}>
                        <TableCell className="font-medium">{item.month_name}</TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatCurrency(item.income)}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {formatCurrency(item.expense)}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${item.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(item.profit)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={item.margin_percent >= 50 ? 'default' : item.margin_percent >= 0 ? 'secondary' : 'destructive'}>
                            {item.income ? `${item.margin_percent}%` : '—'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bar Chart */}
        <Card className="lg:col-span-2 p-6">
          <CardHeader className="p-0 pb-6">
            <CardTitle className="font-heading text-lg">Pendapatan per Bulan</CardTitle>
          </CardHeader>
          <CardContent className="p-0 h-[350px]">
            {loading ? (
              <Skeleton className="w-full h-full" />
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))" 
                    fontSize={12}
                    tickFormatter={(value) => `${(value / 1000000).toFixed(0)}jt`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.75rem'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="Sewa" stackId="a" fill={COLORS[0]} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Listrik" stackId="a" fill={COLORS[1]} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Air" stackId="a" fill={COLORS[2]} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Lainnya" stackId="a" fill={COLORS[3]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Belum ada data untuk ditampilkan
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card className="p-6">
          <CardHeader className="p-0 pb-6">
            <CardTitle className="font-heading text-lg">Komposisi Pendapatan</CardTitle>
          </CardHeader>
          <CardContent className="p-0 h-[350px]">
            {loading ? (
              <Skeleton className="w-full h-full" />
            ) : pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="45%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Belum ada data
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Expense Section */}
      {expenseData && expenseData.total_expense > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Expense Pie Chart */}
          <Card className="p-6">
            <CardHeader className="p-0 pb-6">
              <CardTitle className="font-heading text-lg text-red-600">Komposisi Pengeluaran</CardTitle>
            </CardHeader>
            <CardContent className="p-0 h-[300px]">
              {expensePieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={expensePieData}
                      cx="50%"
                      cy="45%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {expensePieData.map((entry, index) => (
                        <Cell key={`expense-cell-${index}`} fill={['#E07A5F', '#F4A261', '#E76F51', '#264653', '#2A9D8F', '#E9C46A'][index % 6]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Belum ada pengeluaran
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expense Detail by Category */}
          <Card className="lg:col-span-2 p-6">
            <CardHeader className="p-0 pb-6">
              <CardTitle className="font-heading text-lg">Detail Pengeluaran per Kategori</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="space-y-3">
                {expenseData.by_category && Object.entries(expenseData.by_category)
                  .filter(([_, value]) => value > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, amount], index) => (
                    <div key={category} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: ['#E07A5F', '#F4A261', '#E76F51', '#264653', '#2A9D8F', '#E9C46A'][index % 6] }}
                        />
                        <span className="font-medium capitalize">{category}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-red-600">{formatCurrency(amount)}</p>
                        <p className="text-xs text-muted-foreground">
                          {((amount / expenseData.total_expense) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  ))}
                {(!expenseData.by_category || Object.values(expenseData.by_category).every(v => v === 0)) && (
                  <div className="text-center py-8 text-muted-foreground">
                    Belum ada data pengeluaran
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Detail Table */}
      <Card className="overflow-hidden">
        <CardHeader className="p-6 pb-4">
          <CardTitle className="font-heading text-lg">Detail per Bulan</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : data?.monthly_data?.length > 0 ? (
            <>
              {/* Mobile Cards View */}
              <div className="md:hidden p-4 space-y-3">
                {data.monthly_data.map((item) => (
                  <MobileCard key={item.month} className="bg-muted/30">
                    <MobileCardHeader>
                      <MobileCardTitle className="font-semibold">{formatMonth(item.month)}</MobileCardTitle>
                      <span className="text-lg font-bold text-primary">{formatCurrency(item.total)}</span>
                    </MobileCardHeader>
                    <MobileCardContent>
                      <MobileCardRow label="Sewa" value={formatCurrency(item.sewa)} />
                      <MobileCardRow label="Listrik" value={formatCurrency(item.listrik)} />
                      <MobileCardRow label="Air" value={formatCurrency(item.air)} />
                      <MobileCardRow label="Lainnya" value={formatCurrency(item.lainnya)} />
                    </MobileCardContent>
                  </MobileCard>
                ))}
                {/* Total Card */}
                <MobileCard className="bg-primary/10 border-primary/30">
                  <MobileCardHeader>
                    <MobileCardTitle className="font-bold">Total Keseluruhan</MobileCardTitle>
                    <span className="text-lg font-bold text-primary">{formatCurrency(data.total_income)}</span>
                  </MobileCardHeader>
                  <MobileCardContent>
                    <MobileCardRow label="Sewa" value={formatCurrency(data.by_type?.sewa || 0)} />
                    <MobileCardRow label="Listrik" value={formatCurrency(data.by_type?.listrik || 0)} />
                    <MobileCardRow label="Air" value={formatCurrency(data.by_type?.air || 0)} />
                    <MobileCardRow label="Lainnya" value={formatCurrency(data.by_type?.lainnya || 0)} />
                  </MobileCardContent>
                </MobileCard>
              </div>

              {/* Desktop Table View */}
              <Table className="data-table hidden md:table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Bulan</TableHead>
                    <TableHead className="text-right">Sewa</TableHead>
                    <TableHead className="text-right">Listrik</TableHead>
                    <TableHead className="text-right">Air</TableHead>
                    <TableHead className="text-right">Lainnya</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.monthly_data.map((item) => (
                    <TableRow key={item.month}>
                      <TableCell className="font-medium">{formatMonth(item.month)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.sewa)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.listrik)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.air)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.lainnya)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(item.total)}</TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.by_type?.sewa || 0)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.by_type?.listrik || 0)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.by_type?.air || 0)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(data.by_type?.lainnya || 0)}</TableCell>
                    <TableCell className="text-right text-primary">{formatCurrency(data.total_income)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              Belum ada data untuk ditampilkan
            </div>
          )}
        </CardContent>
      </Card>
      </>}
    </div>
  );
};

export default Laporan;
